import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import CONSTANT from "../constant"
import encrypt from "../constant/tablet/encrypt"
import AuthService from "../service/auth/authService"
import { TAuthLoginByPassCodeReq, TAuthLoginByQRCodeReq, TAuthLoginReq, TAuthLoginRes } from "../service/auth/TAuth"
import { TApiResponse } from "../service/TApiResponse"
import encryptUtil from "../ts/util/encryptUtil"

/**
 * ログイン処理
 */
export const login = createAsyncThunk<TAuthLoginRes, TAuthLoginReq, {}>(
  "auth/login",
  async (loginData: TAuthLoginReq, { rejectWithValue }) => {
    try {
      const service = new AuthService()
      const res: TApiResponse<TAuthLoginRes> = await service.login(loginData)

      return res.data
    } catch (error) {
      throw rejectWithValue(error)
    }
  },
)

/**
 * ログイン処理(カード利用)
 */
export const loginByPassCode = createAsyncThunk<TAuthLoginRes, TAuthLoginByPassCodeReq, {}>(
  "auth/login/actions/passcode",
  async (loginData: TAuthLoginByPassCodeReq, { rejectWithValue }) => {
    try {
      const service = new AuthService()
      const res: TApiResponse<TAuthLoginRes> = await service.loginByPassCode(loginData)

      return res.data
    } catch (error) {
      throw rejectWithValue(error)
    }
  },
)

/**
 * ログイン処理(QR利用)
 */
export const loginByQRCode = createAsyncThunk<TAuthLoginRes, TAuthLoginByQRCodeReq, {}>(
  "auth/login/actions/qrcode",
  async (loginData: TAuthLoginByQRCodeReq, { rejectWithValue }) => {
    try {
      // 暗号化して送信する
      const 暗号化済QRコード = encryptUtil.encryptAES(loginData.QRコード, encrypt.KEY, encrypt.IV)
      const service = new AuthService()
      const res: TApiResponse<TAuthLoginRes> = await service.tabletLoginByQRCode({
        QRコード: 暗号化済QRコード,
      })

      return res.data
    } catch (error) {
      throw rejectWithValue(error)
    }
  },
)

/**
 * 点呼者用ログイン処理(カード利用)
 */
export const tenkosyaLoginByPassCode = createAsyncThunk<TAuthLoginRes, TAuthLoginByPassCodeReq, {}>(
  "auth/login/actions/passcode/tenkosya",
  async (loginData: TAuthLoginByPassCodeReq, { rejectWithValue }) => {
    try {
      const service = new AuthService()
      const res: TApiResponse<TAuthLoginRes> = await service.tabletLoginByPassCode(loginData)

      return res.data
    } catch (error) {
      throw rejectWithValue(error)
    }
  },
)

/**
 * 点呼者用ログイン処理(QR利用)
 */
export const tenkosyaLoginByQRCode = createAsyncThunk<TAuthLoginRes, TAuthLoginByQRCodeReq, {}>(
  "auth/login/actions/qrcode/tenkosya",
  async (loginData: TAuthLoginByQRCodeReq, { rejectWithValue }) => {
    try {
      // 暗号化して送信する
      const 暗号化済QRコード = encryptUtil.encryptAES(loginData.QRコード, encrypt.KEY, encrypt.IV)
      const service = new AuthService()
      const res: TApiResponse<TAuthLoginRes> = await service.loginByQRCode({
        QRコード: 暗号化済QRコード,
      })

      return res.data
    } catch (error) {
      throw rejectWithValue(error)
    }
  },
)

/**
 * ログアウト処理
 */
export const logout = createAsyncThunk<TApiResponse<null>, undefined, {}>(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const service = new AuthService()
      return await service.logout()
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

const initLoginUserInfo = {
  社員ID: 0,
  社員コード: "string",
  社員区分ID: 0,
  社員区分コード: "string",
  社員区分名称1: "string",
  社員氏名: "string",
  社員氏名カナ: "string",
  部所ID: 0,
  部所コード: "string",
  部所名: "string",
  部所略称: "string",
  運行部所フラグ: 0,
  営業所ID: 0,
  営業所コード: "string",
  営業所名: "string",
  営業所略称: "string",
  営業所管ID: 0,
  営業所管コード: "string",
  営業所管名: "string",
  部所権限コード: "string",
  打刻監視担当フラグ: 0,
  打刻監視変更可能フラグ: 0,
}

const initialState: TAuthLoginRes = {
  Token: "",
  LoginUserInfo: initLoginUserInfo,
}

//localstorageへ確認、存在していれば初期値として利用
const token = localStorage.getItem(CONSTANT.LOCALSTORAGE_PROPERTIES.AUTH_TOKEN)
const userInfo = localStorage.getItem(CONSTANT.LOCALSTORAGE_PROPERTIES.USER_INFO)

const userInfoJson = (userInfo: string | null) => {
  try {
    return userInfo != null ? JSON.parse(userInfo) : initLoginUserInfo
  } catch (error) {
    return initLoginUserInfo
  }
}

const firstState: TAuthLoginRes = {
  Token: token ?? "",
  LoginUserInfo: userInfoJson(userInfo),
}

const authSlice = createSlice({
  name: "auth",
  initialState: firstState,
  reducers: {
    initState: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        // authTokenはローカルストレージに入れる
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.AUTH_TOKEN, action.payload.Token)
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.USER_INFO, JSON.stringify(action.payload.LoginUserInfo))
        return { ...state, ...action.payload }
      })
      .addCase(login.rejected, (state, action) => {
        //ログイン失敗時はlocalStorageのデータも含めてすべて削除
        localStorage.clear()
        throw action.payload
      })
      .addCase(loginByPassCode.fulfilled, (state, action) => {
        // authTokenはローカルストレージに入れる
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.AUTH_TOKEN, action.payload.Token)
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.USER_INFO, JSON.stringify(action.payload.LoginUserInfo))
        return { ...state, ...action.payload }
      })
      .addCase(loginByPassCode.rejected, (state, action) => {
        //ログイン失敗時はlocalStorageのデータも含めてすべて削除
        localStorage.clear()
        throw action.payload
      })
      .addCase(loginByQRCode.fulfilled, (state, action) => {
        // authTokenはローカルストレージに入れる
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.AUTH_TOKEN, action.payload.Token)
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.USER_INFO, JSON.stringify(action.payload.LoginUserInfo))
        return { ...state, ...action.payload }
      })
      .addCase(loginByQRCode.rejected, (state, action) => {
        //ログイン失敗時はlocalStorageのデータも含めてすべて削除
        localStorage.clear()
        throw action.payload
      })
      .addCase(tenkosyaLoginByPassCode.fulfilled, (state, action) => {
        // authTokenはローカルストレージに入れる
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.AUTH_TOKEN, action.payload.Token)
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.USER_INFO, JSON.stringify(action.payload.LoginUserInfo))
        return { ...state, ...action.payload }
      })
      .addCase(tenkosyaLoginByPassCode.rejected, (state, action) => {
        //ログイン失敗時はlocalStorageのデータも含めてすべて削除
        localStorage.clear()
        throw action.payload
      })
      .addCase(tenkosyaLoginByQRCode.fulfilled, (state, action) => {
        // authTokenはローカルストレージに入れる
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.AUTH_TOKEN, action.payload.Token)
        localStorage.setItem(CONSTANT.LOCALSTORAGE_PROPERTIES.USER_INFO, JSON.stringify(action.payload.LoginUserInfo))
        return { ...state, ...action.payload }
      })
      .addCase(tenkosyaLoginByQRCode.rejected, (state, action) => {
        //ログイン失敗時はlocalStorageのデータも含めてすべて削除
        localStorage.clear()
        throw action.payload
      })
      .addCase(logout.fulfilled, (state, action) => {
        //ログアウト時は失敗成功かかわらずlocalStorageのデータも含めてすべて削除
        localStorage.clear()
        return { ...state, ...initialState }
      })
      .addCase(logout.rejected, (state, action) => {
        //ログアウト時は失敗成功かかわらずlocalStorageのデータも含めてすべて削除
        localStorage.clear()
        return { ...state, ...initialState }
      })
  },
})

export default authSlice
